import { apolloClient } from '../vue-apollo'
import { queryDynamicRaceData } from '../common/queries'
import { statKeyMatches } from '../common/methods'

export const compareDynamicRaceData = (oldData, newData) => {
  if (!oldData || !newData) return

  const topLevelFields = ['do_meeting_id', 'event_id', 'status', 'state', 'venue', 'type', 'no', 'advertised_start', 'exchange_type']
  topLevelFields.forEach(field => {
    const oldVal = oldData[field]
    const newVal = newData[field]
    if (oldVal !== newVal) {
      // console.warn(`${field}:`, { old: oldVal, new: newVal })
    }
  })

  const oldRunners = oldData.runners || []
  const newRunners = newData.runners || []
  const runnerFields = [
    'no', 'status', 'api', 'dsls', 'gear', 'blinkers', 'last_start_beaten_by',
    'barrier_live', 'strike_rate_first', 'strike_rate_second', 'strike_rate_third',
    'strike_rate_place', 'jockey_stats_year', 'trainer_stats_year', 'trainer_location',
    'last_4', 'last_10'
  ]

  oldRunners.forEach((oldRunner) => {
    const newRunner = newRunners.find(r => r.no === oldRunner.no)
    if (!newRunner) return

    const differences = []
    runnerFields.forEach(field => {
      const oldVal = oldRunner[field]
      const newVal = newRunner[field]
      let oldCompare = oldVal
      let newCompare = newVal
      if (typeof oldVal === 'boolean' && typeof newVal === 'boolean') {
        oldCompare = oldVal
        newCompare = newVal
      } else if (typeof oldVal === 'number' && typeof newVal === 'number') {
        oldCompare = Math.round(oldVal * 100) / 100
        newCompare = Math.round(newVal * 100) / 100
      }
      if (oldCompare !== newCompare) {
        differences.push({ field, old: oldVal, new: newVal })
      }
    })
  })
}

export const transformCoreRaceDataToDynamicFormat = (graphqlData) => {
  if (!graphqlData || !graphqlData.race) {
    return null
  }

  const race = graphqlData.race
  const meeting = race.meeting || {}
  const track = meeting.track || {}
  const gbsId = meeting.externalIDs && meeting.externalIDs.length > 0 ? meeting.externalIDs[0].id : null

  const competitors = race.competitors || []
  // Deltafusor compatibility shim: derive `scratched` from scratchingType.
  competitors.forEach(c => {
    c.scratched = c.scratchingType && c.scratchingType !== 'STARTER'
  })
  const nonScratchedBarriers = competitors
    .filter(c => !c.scratched)
    .map(c => c.barrier)
    .sort((a, b) => a - b)

  const runners = competitors.map(competitor => {
    const runnerStats = competitor.runnerStatistics || {}
    const careerAll = runnerStats.career?.all || {}
    const recent = runnerStats.recent || {}
    const formHistory = competitor.formHistory || []
    // Prefer all-time trainer stats — thisSeason is often empty/sparse early
    // in a season; fall back to it only when `all` has no runs.
    const trainerStatsAll = competitor.trainerStatistics?.all || {}
    const trainerStatsThisSeason = trainerStatsAll.numberOfRuns
      ? trainerStatsAll
      : (competitor.trainerStatistics?.thisSeason || {})
    const jockey = competitor.jockey || ''
    const jockeyStatsCareerAll = competitor.jockeyStatistics?.career?.all || {}
    const jockeyStatsAll = competitor.jockeyStatistics?.all || {}

    let jockeyStats = {
      firsts: jockeyStatsCareerAll.firsts || jockeyStatsAll.firsts || 0,
      numberOfRuns: jockeyStatsCareerAll.numberOfRuns || jockeyStatsAll.numberOfRuns || 0,
      seconds: jockeyStatsCareerAll.seconds || jockeyStatsAll.seconds || 0,
      thirds: jockeyStatsCareerAll.thirds || jockeyStatsAll.thirds || 0
    }
    if (jockeyStats.numberOfRuns === 0) {
      const byJockey = runnerStats.career?.detail?.byJockey || []
      if (byJockey.length > 0 && jockey) {
        for (const v of byJockey) {
          // key is a JSON blob like '{"name":"MARK ZAHRA"}'
          if (statKeyMatches(v.key, jockey)) {
            jockeyStats = {
              firsts: v.firsts || 0,
              numberOfRuns: v.numberOfRuns || 0,
              seconds: v.seconds || 0,
              thirds: v.thirds || 0
            }
            break
          }
        }
      }
    }

    const numberOfRuns = careerAll.numberOfRuns || 0
    const seconds = careerAll.seconds || 0
    const thirds = careerAll.thirds || 0
    const calculatedAPI = numberOfRuns > 0 && competitor.prizeMoney != null
      ? Math.round((competitor.prizeMoney / numberOfRuns / 1000) * 10) / 10
      : 0
    const strikeRateSecond = numberOfRuns > 0 && seconds > 0
      ? Math.round((seconds / numberOfRuns) * 100 * 100) / 100
      : 0
    const strikeRateThird = numberOfRuns > 0 && thirds > 0
      ? Math.round((thirds / numberOfRuns) * 100 * 100) / 100
      : 0
    const last10Starts = recent.last10Starts || ''
    const last4 = last10Starts && last10Starts.length >= 4 ? last10Starts.slice(-4) : ''
    const lastStartBeatenBy = Array.isArray(formHistory) && formHistory.length > 0 && formHistory[0].margin
      ? formHistory[0].margin
      : 0
    const trainerStatsYear = trainerStatsThisSeason.numberOfRuns
      ? `${trainerStatsThisSeason.numberOfRuns}: ${trainerStatsThisSeason.firsts || 0}-${trainerStatsThisSeason.seconds || 0}-${trainerStatsThisSeason.thirds || 0}`
      : ''
    const jockeyStatsYear = jockeyStats.numberOfRuns
      ? `${jockeyStats.numberOfRuns}: ${jockeyStats.firsts || 0}-${jockeyStats.seconds || 0}-${jockeyStats.thirds || 0}`
      : ''

    return {
      no: competitor.tabNo || 0,
      status: competitor.scratched ? 'Scratched' : 'Active',
      api: calculatedAPI > 0
        ? calculatedAPI
        : (careerAll.roundedAveragePrizeMoneyWon != null
          ? careerAll.roundedAveragePrizeMoneyWon
          : (careerAll.averagePrizeMoneyWon != null
            ? careerAll.averagePrizeMoneyWon
            : (careerAll.totalPrizeMoneyWon != null && numberOfRuns > 0
              ? Math.round((careerAll.totalPrizeMoneyWon / numberOfRuns / 1000) * 10) / 10
              : 0))),
      dsls: competitor.daysSinceLastRun || 0,
      gear: competitor.gearChangesDescription || '',
      aap_prepost: competitor.aap_prepost || 0,
      blinkers: competitor.blinkers || false,
      last_start_beaten_by: lastStartBeatenBy,
      foal_date: '',
      jockey_colours: '',
      runner_colour: '',
      barrier_live: competitor.scratched ? 0 : nonScratchedBarriers.indexOf(competitor.barrier) + 1,
      strike_rate_first: careerAll.winPercentage || 0,
      strike_rate_second: strikeRateSecond,
      strike_rate_third: strikeRateThird,
      strike_rate_place: careerAll.placePercentage || 0,
      jockey_stats_year: jockeyStatsYear,
      trainer_stats_year: trainerStatsYear,
      trainer_location: competitor.trainingLocation || '',
      stats: {},
      last_4: last4,
      last_10: last10Starts || '',
      rating_champion_bets: 0,
      rating_win_form: 0,
      rating_winning_edge: 0
    }
  })

  const eventId = gbsId && race.number ? `${gbsId}_${String(race.number).padStart(2, '0')}` : null

  return {
    do_meeting_id: gbsId || '',
    event_id: eventId || '',
    status: race.status || 'UNKNOWN',
    state: track.state || '',
    venue: track.name || '',
    type: meeting.type || '',
    no: race.number || 0,
    advertised_start: race.startTime || '',
    runners: runners
  }
}

export const getDynamicRaceData = async function (raceId) {
  try {
    const result = await apolloClient.query({
      query: queryDynamicRaceData(raceId)
    })

    const rawData = result.data
    if (!rawData) return null

    enrichCompetitorsWithAAP(rawData)
    const transformedData = transformCoreRaceDataToDynamicFormat(rawData)
    if (!transformedData) return null

    transformedData.exchange_type = 1.0

    return { transformedData, rawData }
  } catch (error) {
    return null
  }
}

// Map AAP WIN_FIXED_ODDS prices onto each competitor as `aap_prepost`.
// Mirrors the enrichment that srv.apicache used to do server-side.
const enrichCompetitorsWithAAP = (data) => {
  const race = data?.race
  if (!race) return
  const aapPrices = {}
  for (const ps of race.priceSets || []) {
    if (ps.source !== 'AAP' || ps.type !== 'WIN_FIXED_ODDS') continue
    for (const p of ps.prices || []) {
      if (p.tabNo > 0) aapPrices[p.tabNo] = p.price
    }
  }
  for (const c of race.competitors || []) {
    c.aap_prepost = aapPrices[c.tabNo] || 0
  }
  delete race.priceSets
}
